import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useQuery, gql } from '@zenbusiness/application-commons-graphql';
import SharedAsset from '@zenbusiness/common-components/SharedAsset';
import Text from '@zenbusiness/common-components/Text';
import Title from '@zenbusiness/common-components/Title';
import { useParams, Link as RouterLink } from 'react-router-dom';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import ExternalLink from '@mui/material/Link';
import { styled } from '@mui/material/styles';

import Breadcrumbs from '#client/components/Breadcrumbs';
import domainPrivacyData from '#client/components/Modals/constants/domainNamePrivacy';
import PrivacyMap from '#client/components/PrivacyMap';
import manifest from '#client/manifest';

const StyledBoxRoot = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(2)
}));

const StyledGridMastHead = styled(Grid)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(6)
}));

const StyledGridSection = styled(Grid)(({ theme }) => ({
  marginTop: theme.spacing(2.5),
  marginBottom: theme.spacing(2.5)
}));

const StyledBoxSection = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2.5),
  marginBottom: theme.spacing(2.5),
  gap: theme.spacing(3)
}));

const StyledSharedAssetIcon = styled(SharedAsset)(({ theme }) => ({
  margin: '0 auto',
  display: 'block',
  height: theme.spacing(15)
}));

const StyledTitleFeatureTitle = styled(Title)(({ theme }) => ({
  marginTop: theme.spacing(4)
}));

const StyledTitleHeader = styled(Title)(({ theme }) => ({
  ...theme.typography.title_extraLarge,
  margin: theme.spacing(7.5, 0, 2.5)
}));

const StyledGridCtaMap = styled(Grid)(({ theme }) => ({
  minWidth: theme.spacing(40.5),
  maxWidth: theme.spacing(45)
}));

const StyledGridLeadingSection = styled(Grid)(({ theme }) => ({
  marginBottom: theme.spacing(2.5)
}));

const StyledGridCtaContainer = styled(Grid)(({ theme }) => ({
  '&.enabled': {
    marginTop: theme.spacing(3)
  }
}));

const StyledTitleCtaHeader = styled(Title)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  textAlign: 'center',
  ...theme.typography.title_extraLarge
}));

const StyledTextCtaContent = styled(Text)(() => ({
  textAlign: 'center'
}));

export const DOMAIN_PRIVACY_READ = gql`
  query DomainPrivacy($domainUuid: UUID!) {
    domain(domainUuid: $domainUuid) {
      domainUuid
      domainName
      domainPrivacy
      srsExpireDatetime
      domainContacts {
        domainContactUuid
        contactType
        firstName
        lastName
        address1
        address2
        city
        state
        zipCode
        # country
        phone
        email
        editable
        updatedDatetime
      }
    }
  }
`;

const DomainPrivacy = () => {
  const { businessEntityUuid } = useBusinessEntity();
  const { domainUuid } = useParams();

  const { loading, data } = useQuery(DOMAIN_PRIVACY_READ, {
    variables: {
      domainUuid,
      businessEntityUuid
    }
  });

  const domain = loading ? {} : data.domain;
  const ownerContact = domain.domainContacts && domain.domainContacts.find(({ contactType: type }) => (type === 'owner'));

  return (
    <StyledBoxRoot data-testid="domainPrivacy">
      <Breadcrumbs gutterBottom>
        <Button variant="text" color="primary" component={RouterLink} to={`/d/company/${businessEntityUuid}/marketing-tools`}>Overview</Button>
        <Button variant="text" color="primary" component={RouterLink} to={`/d/company/${businessEntityUuid}/marketing-tools/domains`}>Domains</Button>
        <Button variant="text" color="primary" component={RouterLink} to={`/d/company/${businessEntityUuid}/marketing-tools/domains/${domainUuid}`}>Domain</Button>
        <Button variant="text" disabled color="primary" component={RouterLink} to={`/d/company/${businessEntityUuid}/marketing-tools/domains/${domainUuid}/privacy`}>Privacy</Button>
      </Breadcrumbs>

      <StyledGridMastHead
        container
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Grid item>
          <Title level={2} variant="lg-alt">
            {domainPrivacyData.header}
          </Title>
        </Grid>
      </StyledGridMastHead>

      <Grid container item xs={12} md={8}>

        {domain && !domain.domainPrivacy && (
          <>
            <StyledGridLeadingSection
              container
              direction="row"
              justifyContent="center"
              alignItems="flex-start"
              spacing={8}
            >
              <StyledGridCtaMap item xs={12} md={5}>
                {!loading && (
                  <PrivacyMap
                    domainPrivacy={domain.domainPrivacy}
                    contact={ownerContact}
                  />
                )}
              </StyledGridCtaMap>
              <StyledGridCtaContainer item xs={12} md={12}>
                <StyledTitleCtaHeader level={3} variant="lg-alt" align="center" gutterBottom>
                  About Domain Privacy
                </StyledTitleCtaHeader>
                <StyledTextCtaContent size="md" color="focus50" align="center">
                  The address listed is currently publicly listed in the ICANN database.
                  To keep your information private and use ours instead you will need to purchase
                  Domain Privacy for&nbsp;
                  <Text size="xl" bold color="peace40" tag="span">$10 per year</Text>
                  .
                </StyledTextCtaContent>
              </StyledGridCtaContainer>
            </StyledGridLeadingSection>
            <Divider />
            <StyledGridSection
              container
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              spacing={5}
            >
              <Grid item xs={12}>
                <StyledTitleHeader level={2} variant="lg-alt" align="center" gutterBottom>
                  How do I purchase the domain privacy service?
                </StyledTitleHeader>
              </Grid>
              <Grid item sm={12} md={6}>
                <Title level={6} variant="sm" align="center" gutterBottom>Call Us</Title>
                <ExternalLink href="tel:1-844-493-6249" target="_blank" rel="noopener">
                  <Text size="xl" bold color="peace40" align="center" gutterBottom>
                    1-844-4-ZENBIZ
                  </Text>
                </ExternalLink>
                <Text size="lg" color="focus50" align="center">
                  {`You can call our team during business hours ${manifest.contactHours}.`}
                </Text>
              </Grid>
              <Grid item sm={12} md={6}>
                <Title level={6} variant="sm" align="center" gutterBottom>Send Us an Email</Title>
                <ExternalLink href="mailto:support@zenbusiness.com" target="_blank" rel="noopener">
                  <Text size="lg" bold color="peace40" align="center" gutterBottom>
                    support@zenbusiness.com
                  </Text>
                </ExternalLink>
                <Text size="lg" color="focus50" align="center">
                  Send an email to our customer success team and we will reply
                  within 1 business day.
                </Text>
              </Grid>
            </StyledGridSection>
            <Divider />
          </>
        )}

        <StyledGridSection container spacing={5}>
          {
            domainPrivacyData.benefitsSection.map(({ image, subHeader, description }) => (
              <Grid item md key={subHeader}>
                <StyledSharedAssetIcon src={image} width={80} alt="privacy" />
                <StyledTitleFeatureTitle level={4} variant="sm" align="center" gutterBottom>{subHeader}</StyledTitleFeatureTitle>
                <Text size="md" align="center" color="focus50">
                  {' '}
                  {description}
                  {' '}
                </Text>
              </Grid>
            ))
          }
        </StyledGridSection>

        <Divider />

        <StyledBoxSection display="flex" flexDirection="column">
          <StyledTitleHeader level={3} variant="lg-alt" align="center" gutterBottom>
            {domainPrivacyData.summary.header}
          </StyledTitleHeader>
          { domainPrivacyData.summary.body.map((text, index) => (
            <Text size="md" color="focus60" align="left" key={`summary-${index.toString()}`}>
              {text}
            </Text>
          ))}
        </StyledBoxSection>

        {domain && domain.domainPrivacy && (
          <>
            <StyledGridSection
              container
              justifyContent="center"
              alignItems="flex-start"
              spacing={4}
            >
              <StyledGridCtaMap item xs={12} md={5}>
                {!loading && (
                <PrivacyMap
                  domainPrivacy={domain.domainPrivacy}
                  contact={ownerContact}
                />
                )}
              </StyledGridCtaMap>
              <StyledGridCtaContainer item xs={12} className="enabled">
                <Text size="md" color="focus50" align="center">
                  WARNING: Turning off domain privacy means your address, phone number and email
                  will be publicly listed in the ICANN database.
                </Text>
              </StyledGridCtaContainer>
            </StyledGridSection>

            <StyledGridSection
              container
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              spacing={5}
            >
              <Grid item xs={12}>
                <StyledTitleHeader level={2} variant="lg-alt" align="center" gutterBottom>
                  How do I turn off the domain privacy service?
                </StyledTitleHeader>
              </Grid>
              <Grid item sm={12} md={6}>
                <Title level={6} variant="sm" align="center" gutterBottom>Call Us</Title>
                <ExternalLink href="tel:1-844-493-6249" target="_blank" rel="noopener">
                  <Text size="xl" bold color="peace40" align="center" gutterBottom>
                    1-844-4-ZENBIZ
                  </Text>
                </ExternalLink>
                <Text size="lg" color="focus50" align="center">
                  {`You can call our team during business hours ${manifest.contactHours}.`}
                </Text>
              </Grid>
              <Grid item sm={12} md={6}>
                <Title level={6} variant="sm" align="center" gutterBottom>Send Us an Email</Title>
                <ExternalLink href="mailto:support@zenbusiness.com" target="_blank" rel="noopener">
                  <Text size="xl" bold color="peace40" align="center" gutterBottom>
                    support@zenbusiness.com
                  </Text>
                </ExternalLink>
                <Text size="lg" color="focus50" align="center">
                  Send an email to our customer success team and we will reply
                  within 1 business day.
                </Text>
              </Grid>
            </StyledGridSection>
          </>
        )}
      </Grid>
    </StyledBoxRoot>
  );
};

export default DomainPrivacy;
